import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Table,
  Badge,
  Button,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Pagination,
  PaginationItem,
  PaginationLink,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import CommonHeader from "components/Headers/CommonHeader.js";
import CustomDropdown from "components/Common/CustomDropdown";
import CustomPagination from "components/Common/CustomPagination";
import {
  getAllBookings,
  getBookingDetailsById,
  updateBookingStatus,
} from "../../api/ApiServices";
import Swal from "sweetalert2";

const ITEMS_PER_PAGE = 10;

const STATUS_FILTER_OPTIONS = [
  { label: "All Status", value: "All" },
  { label: "Pending", value: "pending" },
  { label: "Accepted", value: "accepted" },
  { label: "Completed", value: "completed" },
  { label: "Cancelled", value: "cancelled" },
  { label: "Rejected", value: "rejected" },
];

const GuideBookingManagement = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [bookingTypeTab, setBookingTypeTab] = useState("all"); // 'all' | 'tour_package' | 'direct_guide'
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [summary, setSummary] = useState({
    total: 0,
    pending: 0,
    accepted: 0,
    completed: 0,
    cancelled: 0,
    rejected: 0,
    tourPackage: 0,
    directGuide: 0,
  });

  const handleBookingTypeChange = (tab) => {
    if (tab === bookingTypeTab) return;

    setLoading(true);
    setBookingTypeTab(tab);
    setCurrentPage(1);
  };

  // View Modal State
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [modalActiveTab, setModalActiveTab] = useState("3"); // Default to Booking Details

  // Status Update Loading State
  const [actionLoading, setActionLoading] = useState(false);

  // Fetch bookings list
  const fetchBookings = useCallback(
    async (page = 1, search = "") => {
      setLoading(true);
      try {
        const params = {
          page,
          limit: ITEMS_PER_PAGE,
          search,
          status: statusFilter !== "All" ? statusFilter : undefined,
          bookingType: bookingTypeTab !== "all" ? bookingTypeTab : undefined,
        };

        const response = await getAllBookings(params);

        if (response && response.success) {
          setBookings(response.data || []);
          setTotalItems(response.pagination?.totalRecords || 0);
          setTotalPages(response.pagination?.totalPages || 1);
          setCurrentPage(response.pagination?.currentPage || 1);
          if (response.summary) {
            setSummary(response.summary);
          }
        } else {
          setBookings([]);
          setTotalItems(0);
          setTotalPages(1);
        }
      } catch (error) {
        console.error("Error fetching bookings:", error);
        setBookings([]);
        setTotalItems(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
        setIsInitialLoad(false);
      }
    },
    [statusFilter, bookingTypeTab]
  );

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchBookings(currentPage, searchTerm);
    }, searchTerm ? 300 : 0);

    return () => clearTimeout(delayDebounce);
  }, [currentPage, searchTerm, fetchBookings]);

  // View booking details
  const handleOpenViewModal = async (booking) => {
    setSelectedBooking(booking);
    setModalActiveTab("3"); // Open booking details tab first
    setViewModalOpen(true);
    setViewLoading(true);
    try {
      const response = await getBookingDetailsById(booking.uuid);
      if (response && response.success && response.data) {
        setSelectedBooking(response.data);
      }
    } catch (error) {
      console.error("Error fetching booking details:", error);
    } finally {
      setViewLoading(false);
    }
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    setSelectedBooking(null);
  };

  // Update Status handler
  const handleStatusChange = async (uuid, newStatus) => {
    const statusLabels = {
      accepted: "Accept",
      completed: "Mark as Completed",
      rejected: "Reject",
    };

    const confirmResult = await Swal.fire({
      title: `${statusLabels[newStatus] || "Update"} Booking?`,
      text: `Are you sure you want to change booking status to "${newStatus.toUpperCase()}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: newStatus === "rejected" ? "#f5365c" : "#5e72e4",
      cancelButtonColor: "#8898aa",
      confirmButtonText: `Yes, ${newStatus}!`,
    });

    if (confirmResult.isConfirmed) {
      setActionLoading(true);
      try {
        const response = await updateBookingStatus(uuid, {
          status: newStatus,
        });

        if (response && response.success) {
          Swal.fire({
            icon: "success",
            title: "Updated!",
            text: response.message || `Booking status updated to ${newStatus}.`,
          });
          fetchBookings(currentPage, searchTerm);
          if (viewModalOpen) {
            setSelectedBooking((prev) => {
              if (!prev) return prev;
              const bookingObj = prev.booking || prev;
              return {
                ...prev,
                status: newStatus,
                booking: {
                  ...bookingObj,
                  status: newStatus,
                },
              };
            });
          }
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response?.message || "Failed to update status",
          });
        }
      } catch (error) {
        console.error("Error updating booking status:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error?.message || "Something went wrong",
        });
      } finally {
        setActionLoading(false);
      }
    }
  };

  // Cancel Booking with Reason prompt
  const handleCancelBooking = async (uuid) => {
    const { value: formValues } = await Swal.fire({
      title: "Cancel Booking",
      html:
        '<label class="text-muted d-block text-left mb-1" style="font-size: 13px;">Cancellation Reason (Optional):</label>' +
        '<textarea id="swal-cancel-reason" class="swal2-textarea m-0 w-100" style="font-size: 13px;" placeholder="Enter cancellation reason..."></textarea>',
      focusConfirm: false,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f5365c",
      cancelButtonColor: "#8898aa",
      confirmButtonText: "Cancel Booking",
      preConfirm: () => {
        const reason = document.getElementById("swal-cancel-reason")?.value;
        return { cancellationReason: reason || "Cancelled by admin" };
      },
    });

    if (formValues) {
      setActionLoading(true);
      try {
        const response = await updateBookingStatus(uuid, {
          status: "cancelled",
          cancelledBy: "admin",
          cancellationReason: formValues.cancellationReason,
        });

        if (response && response.success) {
          Swal.fire({
            icon: "success",
            title: "Cancelled!",
            text: "Booking has been cancelled.",
          });
          fetchBookings(currentPage, searchTerm);
          if (viewModalOpen) {
            setSelectedBooking((prev) => {
              if (!prev) return prev;
              const bookingObj = prev.booking || prev;
              return {
                ...prev,
                status: "cancelled",
                cancelledBy: "admin",
                cancellationReason: formValues.cancellationReason,
                booking: {
                  ...bookingObj,
                  status: "cancelled",
                  cancelledBy: "admin",
                  cancellationReason: formValues.cancellationReason,
                },
              };
            });
          }
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response?.message || "Failed to cancel booking",
          });
        }
      } catch (error) {
        console.error("Error cancelling booking:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error?.message || "Something went wrong",
        });
      } finally {
        setActionLoading(false);
      }
    }
  };

  // Helper for Status Badge
  const getBookingStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "accepted":
        return { color: "success", label: "Accepted", icon: "fa-check-circle" };
      case "completed":
        return { color: "primary", label: "Completed", icon: "fa-flag-checkered" };
      case "cancelled":
        return { color: "danger", label: "Cancelled", icon: "fa-times-circle" };
      case "rejected":
        return { color: "dark", label: "Rejected", icon: "fa-ban" };
      case "pending":
      default:
        return { color: "warning", label: "Pending", icon: "fa-clock" };
    }
  };

  // Helper for Booking Type Badge
  const getBookingTypeBadge = (type) => {
    if (type === "tour_package") {
      return {
        color: "primary",
        label: "Tour Package",
        icon: "fa-map-marked-alt",
      };
    }
    return {
      color: "info",
      label: "Direct Guide",
      icon: "fa-user-tie",
    };
  };

  // Date Formatter
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    return isNaN(d.getTime())
      ? "N/A"
      : d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
  };

  // Format Date with Time
  const formatDateTime = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    return isNaN(d.getTime())
      ? "N/A"
      : d.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
  };

  const hasValue = (v) => v !== null && v !== undefined && v !== "";

  // Normalized Booking Data for Modal
  const bookingData = selectedBooking?.booking || selectedBooking || {};
  const travelerData = selectedBooking?.traveler || bookingData.traveler || {};
  const guideData = selectedBooking?.guide || bookingData.guide || {};
  const tourPackageData = selectedBooking?.tourPackage || bookingData.tourPackage || null;
  const ratingsData = selectedBooking?.ratings || bookingData.ratings || [];
  const travelerReview = selectedBooking?.travelerReview || bookingData.travelerReview || null;
  const guideReview = selectedBooking?.guideReview || bookingData.guideReview || null;

  // Skeleton Rows Renderer
  const renderSkeletonRows = () => {
    return Array.from({ length: 6 }).map((_, index) => (
      <tr key={`skeleton-${index}`}>
        {/* Index */}
        <td>
          <span className="skeleton-box" style={{ width: "24px", height: "16px" }} />
        </td>

        {/* Booking Number */}
        <td>
          <span className="skeleton-box skeleton-badge" style={{ width: "95px", height: "24px" }} />
        </td>

        {/* Type (if all tab) */}
        {bookingTypeTab === "all" && (
          <td>
            <span className="skeleton-box skeleton-badge" style={{ width: "90px", height: "24px" }} />
          </td>
        )}

        {/* Traveler */}
        <td>
          <div className="d-flex align-items-center">
            <span className="skeleton-box skeleton-avatar mr-2" style={{ width: "32px", height: "32px" }} />
            <div>
              <span className="skeleton-box d-block mb-1" style={{ width: "100px", height: "14px" }} />
              <span className="skeleton-box d-block" style={{ width: "70px", height: "11px" }} />
            </div>
          </div>
        </td>

        {/* Guide */}
        <td>
          <div className="d-flex align-items-center">
            <span className="skeleton-box skeleton-avatar mr-2" style={{ width: "32px", height: "32px" }} />
            <div>
              <span className="skeleton-box d-block mb-1" style={{ width: "100px", height: "14px" }} />
              <span className="skeleton-box d-block" style={{ width: "70px", height: "11px" }} />
            </div>
          </div>
        </td>

        {/* Tour / Meeting Point */}
        <td>
          <div>
            <span className="skeleton-box d-block mb-1" style={{ width: "130px", height: "14px" }} />
            <span className="skeleton-box d-block" style={{ width: "80px", height: "11px" }} />
          </div>
        </td>

        {/* Date & Slot */}
        <td>
          <div>
            <span className="skeleton-box d-block mb-1" style={{ width: "85px", height: "14px" }} />
            <span className="skeleton-box d-block" style={{ width: "65px", height: "11px" }} />
          </div>
        </td>

        {/* Guests */}
        <td>
          <span className="skeleton-box skeleton-badge" style={{ width: "60px", height: "20px" }} />
        </td>

        {/* Total Price */}
        <td>
          <span className="skeleton-box" style={{ width: "65px", height: "16px" }} />
        </td>

        {/* Status */}
        <td>
          <span className="skeleton-box skeleton-badge" style={{ width: "80px", height: "24px" }} />
        </td>

        {/* Actions */}
        <td className="text-center">
          <span className="skeleton-box" style={{ width: "28px", height: "28px", borderRadius: "50%" }} />
        </td>
      </tr>
    ));
  };



  return (
    <>
      <CommonHeader />

      <Container className="mt--7 guide-booking-module" fluid>
        <Row>
          <Col>
            <Card className="shadow border-0">
              {/* ================= HEADER ================= */}
              <CardHeader className="border-0 bg-transparent">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                  <div>
                    <h3 className="mb-0 text-dark font-weight-bold">
                      Booking Management
                    </h3>
                    <small className="text-muted">
                      Monitor and manage tour package reservations and direct guide bookings
                    </small>
                  </div>

                  {/* Summary Counters */}
                  <div className="d-flex align-items-center flex-wrap gap-2">
                    <Badge color="primary" pill className="p-2 px-3">
                      Total: {summary.total || 0}
                    </Badge>
                    <Badge color="success" pill className="p-2 px-3">
                      Accepted: {summary.accepted || 0}
                    </Badge>
                    <Badge color="warning" pill className="p-2 px-3">
                      Pending: {summary.pending || 0}
                    </Badge>
                    <Badge color="info" pill className="p-2 px-3">
                      Completed: {summary.completed || 0}
                    </Badge>
                    <Badge color="danger" pill className="p-2 px-3">
                      Cancelled: {summary.cancelled || 0}
                    </Badge>
                  </div>
                </div>

                {/* ================= BOOKING TYPE TABS ================= */}
                <div className="mt-4">
                  <Nav className="guide-tabs">
                    <NavItem>
                      <NavLink
                        className={`guide-tab ${bookingTypeTab === "all" ? "active" : ""}`}
                        onClick={() => handleBookingTypeChange("all")}
                      >
                        <i className="fas fa-th-list mr-1" />
                        <span>All Bookings</span>
                        <Badge color="secondary" pill className="ml-1 px-2">
                          {summary.total || 0}
                        </Badge>
                      </NavLink>
                    </NavItem>

                    <NavItem>
                      <NavLink
                        className={`guide-tab ${bookingTypeTab === "tour_package" ? "active" : ""}`}
                        onClick={() => handleBookingTypeChange("tour_package")}
                      >
                        <i className="fas fa-map-marked-alt mr-1 text-primary" />
                        <span>Tour Package Bookings</span>
                        <Badge color="primary" pill className="ml-1 px-2">
                          {summary.tourPackage || 0}
                        </Badge>
                      </NavLink>
                    </NavItem>

                    <NavItem>
                      <NavLink
                        className={`guide-tab ${bookingTypeTab === "direct_guide" ? "active" : ""}`}
                        onClick={() => handleBookingTypeChange("direct_guide")}
                      >
                        <i className="fas fa-user-tie mr-1 text-info" />
                        <span>Direct Guide Bookings</span>
                        <Badge color="info" pill className="ml-1 px-2">
                          {summary.directGuide || 0}
                        </Badge>
                      </NavLink>
                    </NavItem>
                  </Nav>
                </div>
              </CardHeader>

              {/* ================= BODY ================= */}
              <CardBody className="pt-0">
                {/* SEARCH & FILTERS BAR */}
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
                  {/* SEARCH */}
                  <div style={{ width: "360px", maxWidth: "100%" }}>
                    <InputGroup className="input-group-alternative shadow-sm rounded">
                      <InputGroupAddon addonType="prepend">
                        <InputGroupText style={{ padding: "8px 12px" }}>
                          <i className="fas fa-search text-muted" />
                        </InputGroupText>
                      </InputGroupAddon>
                      <Input
                        type="text"
                        placeholder="Search by Booking ID, Guide, Traveler, Location..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setCurrentPage(1);
                        }}
                        style={{ height: "40px", fontSize: "13px" }}
                      />
                      {searchTerm && (
                        <InputGroupAddon addonType="append">
                          <InputGroupText
                            onClick={() => {
                              setSearchTerm("");
                              setCurrentPage(1);
                            }}
                            style={{ cursor: "pointer", padding: "8px 12px" }}
                          >
                            <i className="fas fa-times text-muted" />
                          </InputGroupText>
                        </InputGroupAddon>
                      )}
                    </InputGroup>
                  </div>

                  {/* STATUS FILTER */}
                  <div className="d-flex align-items-center gap-3 flex-wrap">
                    <div style={{ width: "170px" }}>
                      <CustomDropdown
                        value={statusFilter}
                        onChange={(val) => {
                          if (val === statusFilter) return;
                          setLoading(true);
                          setStatusFilter(val);
                          setCurrentPage(1);
                        }}
                        options={STATUS_FILTER_OPTIONS}
                        placeholder="Booking Status"
                      />
                    </div>
                  </div>
                </div>

                {/* TABLE */}
                <div className="table-responsive" style={{ overflowX: "auto" }}>
                  <Table className="align-items-center table-flush" hover>
                    <thead className="thead-light">
                      <tr>
                        <th>Sr. No.</th>
                        <th>Booking Number</th>
                        {bookingTypeTab === "all" && <th>Type</th>}
                        <th>Traveler</th>
                        <th>Guide</th>
                        {bookingTypeTab === "tour_package" ? (
                          <th>Tour Package</th>
                        ) : bookingTypeTab === "direct_guide" ? (
                          <th>Meeting Point</th>
                        ) : (
                          <th>Tour / Meeting Point</th>
                        )}
                        <th>Date & Slot</th>
                        <th>Guests</th>
                        <th>Total Price</th>
                        <th>Status</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>

                    <tbody>
                      {loading && isInitialLoad ? (
                        <tr>
                          <td colSpan={bookingTypeTab === "all" ? 11 : 10} className="text-center py-5">
                            <div className="py-4">
                              <i className="fas fa-spinner fa-spin fa-2x gradient-color-loader"></i>
                              <p className="mt-2 text-muted mb-0">Loading bookings...</p>
                            </div>
                          </td>
                        </tr>
                      ) : loading ? (
                        renderSkeletonRows()
                      ) : bookings.length === 0 ? (
                        <tr>
                          <td colSpan={bookingTypeTab === "all" ? 11 : 10} className="text-center py-5">
                            <div className="py-4 text-muted">
                              <i className="fas fa-calendar-times fa-3x mb-3 text-light"></i>
                              <h5 className="font-weight-bold">No Bookings Found</h5>
                              <p className="text-muted mb-0">
                                {searchTerm || statusFilter !== "All" || bookingTypeTab !== "all"
                                  ? "No bookings match the selected filters."
                                  : "No bookings have been created yet."}
                              </p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        bookings.map((item, index) => {
                          const statusBadge = getBookingStatusBadge(item.status);
                          const typeBadge = getBookingTypeBadge(item.bookingType);

                          return (
                            <tr key={item.uuid || item._id || index}>
                              {/* Index */}
                              <td>
                                <span className="font-weight-600 text-muted">
                                  {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                                </span>
                              </td>

                              {/* Booking Number */}
                              <td>
                                <Badge color="light" className="text-primary font-weight-bold px-2 py-1">
                                  <i className="fas fa-hashtag mr-1" />
                                  {item.bookingNumber || "N/A"}
                                </Badge>
                              </td>

                              {/* Booking Type Badge (when viewing All) */}
                              {bookingTypeTab === "all" && (
                                <td>
                                  <Badge color={typeBadge.color} pill className="px-2 py-1">
                                    <i className={`fas ${typeBadge.icon} mr-1`} />
                                    {typeBadge.label}
                                  </Badge>
                                </td>
                              )}

                              {/* Traveler */}
                              <td>
                                <div className="d-flex align-items-center">
                                  <div
                                    className="avatar avatar-sm rounded-circle mr-2 bg-gradient-primary text-white d-flex align-items-center justify-content-center"
                                    style={{ width: "32px", height: "32px", fontSize: "12px", minWidth: "32px" }}
                                  >
                                    {item.traveler?.fullName
                                      ? item.traveler.fullName.charAt(0).toUpperCase()
                                      : "T"}
                                  </div>
                                  <div>
                                    <span className="font-weight-600 d-block text-dark">
                                      {item.traveler?.fullName || "N/A"}
                                    </span>
                                    <small className="text-muted">
                                      {item.traveler?.mobileNumber || item.traveler?.email || ""}
                                    </small>
                                  </div>
                                </div>
                              </td>

                              {/* Guide */}
                              <td>
                                <div className="d-flex align-items-center">
                                  <div
                                    className="avatar avatar-sm rounded-circle mr-2 bg-gradient-success text-white d-flex align-items-center justify-content-center"
                                    style={{ width: "32px", height: "32px", fontSize: "12px", minWidth: "32px" }}
                                  >
                                    {item.guide?.fullName
                                      ? item.guide.fullName.charAt(0).toUpperCase()
                                      : "G"}
                                  </div>
                                  <div>
                                    <span className="font-weight-600 d-block text-dark">
                                      {item.guide?.fullName || "N/A"}
                                    </span>
                                    <small className="text-muted">
                                      {item.guide?.mobileNumber || item.guide?.email || ""}
                                    </small>
                                  </div>
                                </div>
                              </td>

                              {/* Tour / Meeting Point Details */}
                              {bookingTypeTab === "tour_package" ? (
                                <td>
                                  <span className="font-weight-500 text-dark d-block text-truncate" style={{ maxWidth: "220px" }}>
                                    {item.tourPackage?.packageName || item.packageName || "Tour Package"}
                                  </span>
                                  {item.tourPackage?.location ? (
                                    <small className="d-block text-muted text-truncate" style={{ maxWidth: "220px" }}>
                                      <i className="fas fa-map-marker-alt text-danger mr-1" />
                                      {item.tourPackage.location}
                                    </small>
                                  ) : item.meetingPoint ? (
                                    <small className="d-block text-muted text-truncate" style={{ maxWidth: "220px" }}>
                                      <i className="fas fa-map-marker-alt text-danger mr-1" />
                                      {item.meetingPoint}
                                    </small>
                                  ) : (
                                    <small className="d-block text-muted font-weight-bold">
                                      -
                                    </small>
                                  )}
                                </td>
                              ) : bookingTypeTab === "direct_guide" ? (
                                <td>
                                  {item.meetingPoint ? (
                                    <span className="font-weight-500 text-dark d-block text-truncate" style={{ maxWidth: "220px" }}>
                                      <i className="fas fa-map-marker-alt text-danger mr-1" />
                                      {item.meetingPoint}
                                    </span>
                                  ) : (
                                    <span className="text-muted font-weight-bold d-block">
                                      -
                                    </span>
                                  )}
                                </td>
                              ) : (
                                <td>
                                  {item.bookingType === "tour_package" ? (
                                    <>
                                      <span className="font-weight-500 text-dark d-block text-truncate" style={{ maxWidth: "220px" }}>
                                        {item.tourPackage?.packageName || item.packageName || "Tour Package"}
                                      </span>
                                      {item.tourPackage?.location ? (
                                        <small className="d-block text-muted text-truncate" style={{ maxWidth: "220px" }}>
                                          <i className="fas fa-map-marker-alt text-danger mr-1" />
                                          {item.tourPackage.location}
                                        </small>
                                      ) : item.meetingPoint ? (
                                        <small className="d-block text-muted text-truncate" style={{ maxWidth: "220px" }}>
                                          <i className="fas fa-map-marker-alt text-danger mr-1" />
                                          {item.meetingPoint}
                                        </small>
                                      ) : (
                                        <small className="d-block text-muted font-weight-bold">
                                          -
                                        </small>
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      {item.meetingPoint ? (
                                        <span className="font-weight-500 text-dark d-block text-truncate" style={{ maxWidth: "220px" }}>
                                          <i className="fas fa-map-marker-alt text-danger mr-1" />
                                          {item.meetingPoint}
                                        </span>
                                      ) : (
                                        <span className="text-muted font-weight-bold d-block">
                                          -
                                        </span>
                                      )}
                                    </>
                                  )}
                                </td>
                              )}

                              {/* Date & Slot */}
                              <td>
                                <span className="font-weight-500 d-block">
                                  {formatDate(item.bookingDate || item.tourDate)}
                                </span>
                                <small className="text-muted">
                                  <i className="far fa-clock mr-1" />
                                  {item.startTime && item.endTime
                                    ? `${item.startTime} - ${item.endTime}`
                                    : item.durationHours
                                      ? `${item.durationHours} Hours`
                                      : "Full Day"}
                                </small>
                              </td>

                              {/* Guests */}
                              <td>
                                <span className="font-weight-500 text-dark">
                                  {item.adults || 1} Adult{item.adults > 1 ? "s" : ""}
                                </span>
                                {item.children > 0 && (
                                  <small className="d-block text-muted">
                                    {item.children} Child{item.children > 1 ? "ren" : ""}
                                  </small>
                                )}
                              </td>

                              {/* Total Price */}
                              <td>
                                <span className="font-weight-bold text-primary" style={{ fontSize: "14px" }}>
                                  {item.totalPrice !== undefined ? item.totalPrice : item.totalAmount || 0}
                                </span>
                              </td>

                              {/* Status */}
                              <td>
                                <Badge color={statusBadge.color} pill className="px-2 py-1">
                                  <i className={`fas ${statusBadge.icon} mr-1`} />
                                  {statusBadge.label}
                                </Badge>
                              </td>

                              {/* Actions Dropdown */}
                              <td className="text-center">
                                <UncontrolledDropdown>
                                  <DropdownToggle
                                    className="btn-icon-only text-light"
                                    color=""
                                    role="button"
                                    size="sm"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <i className="fas fa-ellipsis-v text-muted" />
                                  </DropdownToggle>
                                  <DropdownMenu right className="dropdown-menu-arrow shadow">
                                    <DropdownItem
                                      onClick={() => handleOpenViewModal(item)}
                                      className="d-flex align-items-center"
                                    >
                                      <i className="fas fa-eye text-info mr-2" />
                                      View Details
                                    </DropdownItem>

                                    {/*
                                    {item.status === "pending" && (
                                      <>
                                        <DropdownItem
                                          onClick={() => handleStatusChange(item.uuid, "accepted")}
                                          className="d-flex align-items-center text-success"
                                        >
                                          <i className="fas fa-check-circle text-success mr-2" />
                                          Accept Booking
                                        </DropdownItem>
                                        <DropdownItem
                                          onClick={() => handleStatusChange(item.uuid, "rejected")}
                                          className="d-flex align-items-center text-warning"
                                        >
                                          <i className="fas fa-ban text-warning mr-2" />
                                          Reject Booking
                                        </DropdownItem>
                                      </>
                                    )}
                                    */}

                                  </DropdownMenu>
                                </UncontrolledDropdown>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </div>
              </CardBody>

              {/* PAGINATION */}
              {!loading && totalItems > 0 && (
                <CardFooter className="py-4 border-0">
                  <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={totalItems}
                    itemsPerPage={ITEMS_PER_PAGE}
                    onPageChange={(page) => {
                      setLoading(true);
                      setCurrentPage(page);
                    }}
                    itemName="bookings"
                  />
                </CardFooter>
              )}
            </Card>
          </Col>
        </Row>

        {/* ================= VIEW BOOKING DETAILS MODAL ================= */}
        <Modal
          isOpen={viewModalOpen}
          toggle={closeViewModal}
          size="lg"
          className="modal-dialog-centered booking-details-modal"
        >
          <ModalHeader toggle={closeViewModal} className="border-bottom pb-3">
            <div className="d-flex align-items-center">
              <div
                className="bg-primary text-white rounded-circle p-2 mr-3 d-flex align-items-center justify-content-center"
                style={{ width: "42px", height: "42px" }}
              >
                <i className="fas fa-calendar-check" style={{ fontSize: "18px" }} />
              </div>
              <div>
                <h4 className="mb-0 font-weight-bold">
                  {bookingData.bookingNumber
                    ? `Booking ${bookingData.bookingNumber}`
                    : "Booking Details"}
                </h4>
                <small className="text-muted">
                  {bookingData.bookingType === "tour_package"
                    ? "Tour Package Reservation Details"
                    : "Direct Guide Booking Details"}
                </small>
              </div>
            </div>
          </ModalHeader>

          <ModalBody className="pt-3">
            {viewLoading ? (
              <div className="text-center py-5">
                <i className="fas fa-spinner fa-spin fa-2x gradient-color-loader"></i>
                <p className="mt-2 text-muted mb-0">Loading booking details...</p>
              </div>
            ) : selectedBooking ? (
              <>
                {/* 4 TABS NAVIGATION */}
                <Nav className="booking-modal-tabs">
                  <NavItem>
                    <NavLink
                      className={`booking-modal-tab ${modalActiveTab === "3" ? "active" : ""}`}
                      onClick={() => setModalActiveTab("3")}
                    >
                      <i className="fas fa-receipt text-warning mr-2" />
                      <span>Booking Details</span>
                    </NavLink>
                  </NavItem>

                  <NavItem>
                    <NavLink
                      className={`booking-modal-tab ${modalActiveTab === "1" ? "active" : ""}`}
                      onClick={() => setModalActiveTab("1")}
                    >
                      <i className="fas fa-user-tie text-success mr-2" />
                      <span>Guide Details</span>
                    </NavLink>
                  </NavItem>

                  <NavItem>
                    <NavLink
                      className={`booking-modal-tab ${modalActiveTab === "2" ? "active" : ""}`}
                      onClick={() => setModalActiveTab("2")}
                    >
                      <i className="fas fa-user text-primary mr-2" />
                      <span>Traveler Details</span>
                    </NavLink>
                  </NavItem>

                  {false && (
                  <NavItem>
                    <NavLink
                      className={`booking-modal-tab ${modalActiveTab === "4" ? "active" : ""}`}
                      onClick={() => setModalActiveTab("4")}
                    >
                      <i className="fas fa-star text-warning mr-2" />
                      <span>Ratings & Reviews</span>
                      {ratingsData.length > 0 && (
                        <Badge color="warning" pill className="ml-2 px-2 py-1" style={{ fontSize: "10px" }}>
                          {ratingsData.length}
                        </Badge>
                      )}
                    </NavLink>
                  </NavItem>
                  )}
                </Nav>

                {/* TAB CONTENT */}
                <TabContent activeTab={modalActiveTab}>
                  {/* ================= TAB 3: BOOKING DETAILS ================= */}
                  <TabPane tabId="3">
                    <div className="booking-info-card">
                      {/* Top Status & Type Banner */}
                      <div className="bg-secondary p-3 rounded mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-2">
                          <span className="text-muted mr-1" style={{ fontSize: "12px" }}>
                            Status:
                          </span>
                          <Badge
                            color={getBookingStatusBadge(bookingData.status).color}
                            pill
                            className="px-3 py-1"
                          >
                            <i
                              className={`fas ${getBookingStatusBadge(bookingData.status).icon} mr-1`}
                            />
                            {getBookingStatusBadge(bookingData.status).label}
                          </Badge>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                          <span className="text-muted mr-1" style={{ fontSize: "12px" }}>
                            Type:
                          </span>
                          <Badge
                            color={getBookingTypeBadge(bookingData.bookingType).color}
                            pill
                            className="px-3 py-1"
                          >
                            <i
                              className={`fas ${getBookingTypeBadge(bookingData.bookingType).icon} mr-1`}
                            />
                            {getBookingTypeBadge(bookingData.bookingType).label}
                          </Badge>
                        </div>
                      </div>

                      {/* Booking Details Grid */}
                      <div className="booking-detail-grid">
                        {/* Booking Number */}
                        <div className="booking-detail-item">
                          <div className="booking-detail-icon purple">
                            <i className="fas fa-hashtag" />
                          </div>
                          <div className="booking-detail-content">
                            <span className="label">Booking Number</span>
                            <span className="value text-primary font-weight-bold">
                              {bookingData.bookingNumber || "N/A"}
                            </span>
                          </div>
                        </div>

                        {/* Booking Type */}
                        <div className="booking-detail-item">
                          <div className="booking-detail-icon purple">
                            <i className="fas fa-tags" />
                          </div>
                          <div className="booking-detail-content">
                            <span className="label">Booking Type</span>
                            <span className="value">
                              {bookingData.bookingType === "tour_package"
                                ? "Tour Package"
                                : "Direct Guide Booking"}
                            </span>
                          </div>
                        </div>

                        {/* Booking Date */}
                        <div className="booking-detail-item">
                          <div className="booking-detail-icon purple">
                            <i className="far fa-calendar-alt" />
                          </div>
                          <div className="booking-detail-content">
                            <span className="label">Booking Date</span>
                            <span className="value">
                              {formatDate(bookingData.bookingDate || bookingData.tourDate)}
                            </span>
                          </div>
                        </div>

                        {/* Start Time */}
                        <div className="booking-detail-item">
                          <div className="booking-detail-icon purple">
                            <i className="far fa-clock" />
                          </div>
                          <div className="booking-detail-content">
                            <span className="label">Start Time</span>
                            <span className="value">
                              {bookingData.startTime || "N/A"}
                            </span>
                          </div>
                        </div>

                        {/* End Time */}
                        <div className="booking-detail-item">
                          <div className="booking-detail-icon purple">
                            <i className="fas fa-history" />
                          </div>
                          <div className="booking-detail-content">
                            <span className="label">End Time</span>
                            <span className="value">
                              {bookingData.endTime || "N/A"}
                            </span>
                          </div>
                        </div>

                        {/* Duration */}
                        <div className="booking-detail-item">
                          <div className="booking-detail-icon purple">
                            <i className="fas fa-hourglass-half" />
                          </div>
                          <div className="booking-detail-content">
                            <span className="label">Duration</span>
                            <span className="value">
                              {bookingData.durationHours
                                ? `${bookingData.durationHours} Hours`
                                : bookingData.duration || "N/A"}
                            </span>
                          </div>
                        </div>

                        {/* Total Price */}
                        <div className="booking-detail-item">
                          <div className="booking-detail-icon purple">
                            <i className="fas fa-dollar-sign" />
                          </div>
                          <div className="booking-detail-content">
                            <span className="label">Total Price</span>
                            <span className="value text-success font-weight-bold">
                              {bookingData.totalPrice !== undefined ? bookingData.totalPrice : bookingData.totalAmount || 0}
                            </span>
                          </div>
                        </div>

                        {/* Adults */}
                        <div className="booking-detail-item">
                          <div className="booking-detail-icon purple">
                            <i className="fas fa-users" />
                          </div>
                          <div className="booking-detail-content">
                            <span className="label">Adults</span>
                            <span className="value">
                              {bookingData.adults || bookingData.numberOfTravelers || 1}
                            </span>
                          </div>
                        </div>

                        {/* Children */}
                        <div className="booking-detail-item">
                          <div className="booking-detail-icon purple">
                            <i className="fas fa-child" />
                          </div>
                          <div className="booking-detail-content">
                            <span className="label">Children</span>
                            <span className="value">
                              {bookingData.children || 0}
                            </span>
                          </div>
                        </div>

                        {/* Meeting Point */}
                        <div className="booking-detail-item full-width">
                          <div className="booking-detail-icon red">
                            <i className="fas fa-map-marked-alt" />
                          </div>
                          <div className="booking-detail-content">
                            <span className="label">Meeting Point</span>
                            <span className="value">
                              {hasValue(bookingData.meetingPoint) ? (
                                bookingData.meetingPoint
                              ) : (
                                <span className="text-muted font-weight-bold">-</span>
                              )}
                            </span>
                          </div>
                        </div>

                        {/* Special Requests */}
                        {hasValue(bookingData.specialRequests) && (
                          <div className="booking-detail-item full-width">
                            <div className="booking-detail-icon amber">
                              <i className="fas fa-comment-dots" />
                            </div>
                            <div className="booking-detail-content">
                              <span className="label">Special Requests</span>
                              <span className="value">{bookingData.specialRequests}</span>
                            </div>
                          </div>
                        )}

                        {/* Cancelled Info */}
                        {bookingData.status === "cancelled" && (
                          <div className="booking-detail-item full-width bg-light border-danger">
                            <div className="booking-detail-icon red">
                              <i className="fas fa-ban text-danger" />
                            </div>
                            <div className="booking-detail-content">
                              <span className="label text-danger font-weight-bold">
                                Cancelled Information
                              </span>
                              <span className="value text-dark">
                                Cancelled by: <strong className="text-capitalize">{bookingData.cancelledBy || "N/A"}</strong>
                                {bookingData.cancellationReason && (
                                  <> | Reason: {bookingData.cancellationReason}</>
                                )}
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Created At */}
                        <div className="booking-detail-item">
                          <div className="booking-detail-icon blue">
                            <i className="far fa-clock" />
                          </div>
                          <div className="booking-detail-content">
                            <span className="label">Created Date</span>
                            <span className="value">
                              {formatDateTime(bookingData.createdAt)}
                            </span>
                          </div>
                        </div>

                        {/* Guide Responded At */}
                        {hasValue(bookingData.guideRespondedAt) && (
                          <div className="booking-detail-item">
                            <div className="booking-detail-icon green">
                              <i className="fas fa-reply" />
                            </div>
                            <div className="booking-detail-content">
                              <span className="label">Guide Responded At</span>
                              <span className="value">
                                {formatDateTime(bookingData.guideRespondedAt)}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Optional Tour Package Card */}
                      {tourPackageData && (
                        <div className="mt-3 pt-3 border-top">
                          <div className="d-flex align-items-center mb-2">
                            <i className="fas fa-suitcase-rolling text-primary mr-2" />
                            <h5 className="mb-0 font-weight-bold text-dark">
                              Tour Package: {tourPackageData.packageName}
                            </h5>
                          </div>
                          {tourPackageData.location && (
                            <small className="text-muted d-block mb-2">
                              <i className="fas fa-map-marker-alt text-danger mr-1" />
                              {tourPackageData.location}
                              {tourPackageData.duration && (
                                <span className="ml-3">
                                  <i className="far fa-clock mr-1" />
                                  Duration: {tourPackageData.duration}
                                </span>
                              )}
                              {tourPackageData.price && (
                                <span className="ml-3 text-success font-weight-bold">
                                  Price: ${tourPackageData.price}
                                </span>
                              )}
                            </small>
                          )}
                          {tourPackageData.shortDescription && (
                            <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                              {tourPackageData.shortDescription}
                            </p>
                          )}
                        </div>
                      )}

                      {/* ================= RATINGS & REVIEWS (2 PROMINENT DIVS: TRAVELER & GUIDE) ================= */}
                      {false && (
                      <div className="mt-4 pt-3 border-top">
                        <div className="d-flex align-items-center justify-content-between mb-3">
                          <div className="d-flex align-items-center">
                            <div
                              className="rounded-circle d-flex align-items-center justify-content-center mr-2 shadow-sm"
                              style={{ width: "32px", height: "32px", background: "#fef3c7", color: "#d97706" }}
                            >
                              <i className="fas fa-star" />
                            </div>
                            <div>
                              <h5 className="mb-0 font-weight-bold text-dark">
                                Booking Ratings & Reviews
                              </h5>
                              <small className="text-muted">
                                Mutual ratings exchanged between traveler and guide for this booking
                              </small>
                            </div>
                          </div>
                          <div>
                            <Badge color="secondary" pill className="px-2 py-1 text-xs">
                              {ratingsData.length} {ratingsData.length === 1 ? "Review" : "Reviews"} Recorded
                            </Badge>
                          </div>
                        </div>

                        <Row>
                          {/* DIV 1: Traveler's Rating to Guide */}
                          <Col md="6" className="mb-3">
                            <div
                              className="rounded h-100 overflow-hidden"
                              style={{
                                backgroundColor: "#ffffff",
                                border: travelerReview ? "1.5px solid #6366f1" : "1.5px dashed #cbd5e1",
                                boxShadow: travelerReview ? "0 4px 16px rgba(99, 102, 241, 0.1)" : "none",
                              }}
                            >
                              {/* Card Top Header */}
                              <div
                                className="p-3 d-flex align-items-center justify-content-between"
                                style={{
                                  background: travelerReview
                                    ? "linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)"
                                    : "#f8fafc",
                                  borderBottom: "1px solid #e2e8f0",
                                }}
                              >
                                <div className="d-flex align-items-center">
                                  <div
                                    className="rounded-circle d-flex align-items-center justify-content-center mr-2 shadow-sm font-weight-bold"
                                    style={{ width: "28px", height: "28px", background: "#6366f1", color: "#ffffff", fontSize: "12px" }}
                                  >
                                    <i className="fas fa-paper-plane" />
                                  </div>
                                  <div>
                                    <span className="font-weight-bold text-dark d-block" style={{ fontSize: "13px", lineHeight: "1.2" }}>
                                      Traveler ➔ Guide Review
                                    </span>
                                    <small className="text-muted" style={{ fontSize: "11px" }}>
                                      Given by <strong>{travelerData.fullName || "Traveler"}</strong> to <strong>{guideData.fullName || "Guide"}</strong>
                                    </small>
                                  </div>
                                </div>

                                <div>
                                  {travelerReview ? (
                                    <Badge
                                      color={travelerReview.isActive !== false ? "success" : "secondary"}
                                      pill
                                      className="px-2 py-1"
                                    >
                                      {travelerReview.isActive !== false ? "Active" : "Hidden"}
                                    </Badge>
                                  ) : (
                                    <Badge color="light" pill className="text-muted border px-2 py-1" style={{ fontSize: "11px" }}>
                                      Not Rated Yet
                                    </Badge>
                                  )}
                                </div>
                              </div>

                              {/* Card Body */}
                              <div className="p-3">
                                {/* WHO RATED WHOM FLOW */}
                                <div
                                  className="p-2 mb-3 rounded d-flex align-items-center justify-content-between"
                                  style={{
                                    background: travelerReview ? "#f8fafc" : "#fafafa",
                                    border: "1px solid #e2e8f0",
                                  }}
                                >
                                  {/* Giver: Traveler */}
                                  <div className="d-flex align-items-center" style={{ flex: "1 1 0", minWidth: 0 }}>
                                    {travelerData.profileImage ? (
                                      <img
                                        src={travelerData.profileImage}
                                        alt={travelerData.fullName}
                                        className="rounded-circle mr-2 shadow-sm"
                                        style={{ width: "34px", height: "34px", minWidth: "34px", objectFit: "cover" }}
                                      />
                                    ) : (
                                      <div
                                        className="avatar avatar-sm rounded-circle mr-2 bg-gradient-primary text-white d-flex align-items-center justify-content-center shadow-sm font-weight-bold"
                                        style={{ width: "34px", height: "34px", minWidth: "34px", fontSize: "12px" }}
                                      >
                                        {travelerData.fullName ? travelerData.fullName.charAt(0).toUpperCase() : "T"}
                                      </div>
                                    )}
                                    <div className="overflow-hidden">
                                      <span className="font-weight-bold d-block text-dark text-truncate" style={{ fontSize: "12px", lineHeight: "1.2" }}>
                                        {travelerData.fullName || "Traveler"}
                                      </span>
                                      <Badge color="primary" pill style={{ fontSize: "9px", padding: "1px 5px", fontWeight: "600" }}>
                                        Reviewer (Traveler)
                                      </Badge>
                                    </div>
                                  </div>

                                  {/* Arrow Connector */}
                                  <div className="text-center px-1 d-flex flex-column align-items-center" style={{ minWidth: "85px" }}>
                                    <span className="text-muted text-uppercase" style={{ fontSize: "9px", fontWeight: "700" }}>
                                      {travelerReview ? "Rates" : "Pending"}
                                    </span>
                                    <div className="d-flex align-items-center text-primary" style={{ fontSize: "13px" }}>
                                      <span style={{ height: "2px", width: "20px", background: "#6366f1", display: "inline-block" }}></span>
                                      <i className="fas fa-chevron-right" style={{ color: "#6366f1", marginLeft: "-2px" }} />
                                    </div>
                                  </div>

                                  {/* Receiver: Guide */}
                                  <div className="d-flex align-items-center justify-content-end text-right" style={{ flex: "1 1 0", minWidth: 0 }}>
                                    <div className="overflow-hidden mr-2">
                                      <span className="font-weight-bold d-block text-dark text-truncate" style={{ fontSize: "12px", lineHeight: "1.2" }}>
                                        {guideData.fullName || "Guide"}
                                      </span>
                                      <Badge color="success" pill style={{ fontSize: "9px", padding: "1px 5px", fontWeight: "600" }}>
                                        Reviewed (Guide)
                                      </Badge>
                                    </div>
                                    {guideData.profileImage ? (
                                      <img
                                        src={guideData.profileImage}
                                        alt={guideData.fullName}
                                        className="rounded-circle shadow-sm"
                                        style={{ width: "34px", height: "34px", minWidth: "34px", objectFit: "cover" }}
                                      />
                                    ) : (
                                      <div
                                        className="avatar avatar-sm rounded-circle bg-gradient-success text-white d-flex align-items-center justify-content-center shadow-sm font-weight-bold"
                                        style={{ width: "34px", height: "34px", minWidth: "34px", fontSize: "12px" }}
                                      >
                                        {guideData.fullName ? guideData.fullName.charAt(0).toUpperCase() : "G"}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {travelerReview ? (
                                  <div>
                                    {/* Star Rating Score Bar */}
                                    <div
                                      className="p-2 mb-2 rounded d-flex align-items-center justify-content-between"
                                      style={{ background: "#fef3c7", border: "1px solid #fde68a" }}
                                    >
                                      <div className="d-flex align-items-center">
                                        <div className="mr-2">
                                          {Array.from({ length: 5 }).map((_, i) => (
                                            <i
                                              key={i}
                                              className={`fas fa-star ${
                                                i < (travelerReview.rating || 0) ? "text-warning" : "text-light"
                                              }`}
                                              style={{ fontSize: "14px", marginRight: "3px" }}
                                            />
                                          ))}
                                        </div>
                                        <span className="font-weight-bold text-dark" style={{ fontSize: "14px" }}>
                                          {Number(travelerReview.rating).toFixed(1)} / 5.0
                                        </span>
                                      </div>
                                      <small className="text-muted font-weight-500" style={{ fontSize: "11px" }}>
                                        <i className="far fa-clock mr-1" />
                                        {formatDate(travelerReview.createdAt)}
                                      </small>
                                    </div>

                                    {/* Written Review Feedback */}
                                    <div>
                                      <small className="text-muted text-uppercase font-weight-bold d-block mb-1" style={{ fontSize: "10px", letterSpacing: "0.5px" }}>
                                        Feedback / Review Comment:
                                      </small>
                                      <div
                                        className="p-3 rounded bg-white text-dark shadow-xs"
                                        style={{
                                          fontSize: "13px",
                                          lineHeight: "1.6",
                                          border: "1px solid #e2e8f0",
                                          borderLeft: "3px solid #6366f1",
                                          minHeight: "56px",
                                          fontStyle: travelerReview.review ? "normal" : "italic",
                                        }}
                                      >
                                        {travelerReview.review ? (
                                          <>
                                            <i className="fas fa-quote-left text-muted mr-2" style={{ fontSize: "11px" }} />
                                            {travelerReview.review}
                                          </>
                                        ) : (
                                          <span className="text-muted">No written feedback was provided.</span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="text-center py-4 px-2">
                                    <div
                                      className="rounded-circle d-inline-flex align-items-center justify-content-center mb-2"
                                      style={{ width: "42px", height: "42px", background: "#f1f5f9", color: "#94a3b8" }}
                                    >
                                      <i className="far fa-star fa-lg" />
                                    </div>
                                    <h6 className="font-weight-bold text-dark mb-1" style={{ fontSize: "13px" }}>
                                      Traveler Has Not Rated Yet
                                    </h6>
                                    <small className="text-muted d-block" style={{ fontSize: "12px" }}>
                                      <strong>{travelerData.fullName || "Traveler"}</strong> has not submitted a rating or review for <strong>{guideData.fullName || "Guide"}</strong> yet.
                                    </small>
                                  </div>
                                )}
                              </div>
                            </div>
                          </Col>

                          {/* DIV 2: Guide's Rating to Traveler */}
                          <Col md="6" className="mb-3">
                            <div
                              className="rounded h-100 overflow-hidden"
                              style={{
                                backgroundColor: "#ffffff",
                                border: guideReview ? "1.5px solid #10b981" : "1.5px dashed #cbd5e1",
                                boxShadow: guideReview ? "0 4px 16px rgba(16, 185, 129, 0.1)" : "none",
                              }}
                            >
                              {/* Card Top Header */}
                              <div
                                className="p-3 d-flex align-items-center justify-content-between"
                                style={{
                                  background: guideReview
                                    ? "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)"
                                    : "#f8fafc",
                                  borderBottom: "1px solid #e2e8f0",
                                }}
                              >
                                <div className="d-flex align-items-center">
                                  <div
                                    className="rounded-circle d-flex align-items-center justify-content-center mr-2 shadow-sm font-weight-bold"
                                    style={{ width: "28px", height: "28px", background: "#10b981", color: "#ffffff", fontSize: "12px" }}
                                  >
                                    <i className="fas fa-paper-plane" />
                                  </div>
                                  <div>
                                    <span className="font-weight-bold text-dark d-block" style={{ fontSize: "13px", lineHeight: "1.2" }}>
                                      Guide ➔ Traveler Review
                                    </span>
                                    <small className="text-muted" style={{ fontSize: "11px" }}>
                                      Given by <strong>{guideData.fullName || "Guide"}</strong> to <strong>{travelerData.fullName || "Traveler"}</strong>
                                    </small>
                                  </div>
                                </div>

                                <div>
                                  {guideReview ? (
                                    <Badge
                                      color={guideReview.isActive !== false ? "success" : "secondary"}
                                      pill
                                      className="px-2 py-1"
                                    >
                                      {guideReview.isActive !== false ? "Active" : "Hidden"}
                                    </Badge>
                                  ) : (
                                    <Badge color="light" pill className="text-muted border px-2 py-1" style={{ fontSize: "11px" }}>
                                      Not Rated Yet
                                    </Badge>
                                  )}
                                </div>
                              </div>

                              {/* Card Body */}
                              <div className="p-3">
                                {/* WHO RATED WHOM FLOW */}
                                <div
                                  className="p-2 mb-3 rounded d-flex align-items-center justify-content-between"
                                  style={{
                                    background: guideReview ? "#f8fafc" : "#fafafa",
                                    border: "1px solid #e2e8f0",
                                  }}
                                >
                                  {/* Giver: Guide */}
                                  <div className="d-flex align-items-center" style={{ flex: "1 1 0", minWidth: 0 }}>
                                    {guideData.profileImage ? (
                                      <img
                                        src={guideData.profileImage}
                                        alt={guideData.fullName}
                                        className="rounded-circle mr-2 shadow-sm"
                                        style={{ width: "34px", height: "34px", minWidth: "34px", objectFit: "cover" }}
                                      />
                                    ) : (
                                      <div
                                        className="avatar avatar-sm rounded-circle mr-2 bg-gradient-success text-white d-flex align-items-center justify-content-center shadow-sm font-weight-bold"
                                        style={{ width: "34px", height: "34px", minWidth: "34px", fontSize: "12px" }}
                                      >
                                        {guideData.fullName ? guideData.fullName.charAt(0).toUpperCase() : "G"}
                                      </div>
                                    )}
                                    <div className="overflow-hidden">
                                      <span className="font-weight-bold d-block text-dark text-truncate" style={{ fontSize: "12px", lineHeight: "1.2" }}>
                                        {guideData.fullName || "Guide"}
                                      </span>
                                      <Badge color="success" pill style={{ fontSize: "9px", padding: "1px 5px", fontWeight: "600" }}>
                                        Reviewer (Guide)
                                      </Badge>
                                    </div>
                                  </div>

                                  {/* Arrow Connector */}
                                  <div className="text-center px-1 d-flex flex-column align-items-center" style={{ minWidth: "85px" }}>
                                    <span className="text-muted text-uppercase" style={{ fontSize: "9px", fontWeight: "700" }}>
                                      {guideReview ? "Rates" : "Pending"}
                                    </span>
                                    <div className="d-flex align-items-center text-success" style={{ fontSize: "13px" }}>
                                      <span style={{ height: "2px", width: "20px", background: "#10b981", display: "inline-block" }}></span>
                                      <i className="fas fa-chevron-right" style={{ color: "#10b981", marginLeft: "-2px" }} />
                                    </div>
                                  </div>

                                  {/* Receiver: Traveler */}
                                  <div className="d-flex align-items-center justify-content-end text-right" style={{ flex: "1 1 0", minWidth: 0 }}>
                                    <div className="overflow-hidden mr-2">
                                      <span className="font-weight-bold d-block text-dark text-truncate" style={{ fontSize: "12px", lineHeight: "1.2" }}>
                                        {travelerData.fullName || "Traveler"}
                                      </span>
                                      <Badge color="primary" pill style={{ fontSize: "9px", padding: "1px 5px", fontWeight: "600" }}>
                                        Reviewed (Traveler)
                                      </Badge>
                                    </div>
                                    {travelerData.profileImage ? (
                                      <img
                                        src={travelerData.profileImage}
                                        alt={travelerData.fullName}
                                        className="rounded-circle shadow-sm"
                                        style={{ width: "34px", height: "34px", minWidth: "34px", objectFit: "cover" }}
                                      />
                                    ) : (
                                      <div
                                        className="avatar avatar-sm rounded-circle bg-gradient-primary text-white d-flex align-items-center justify-content-center shadow-sm font-weight-bold"
                                        style={{ width: "34px", height: "34px", minWidth: "34px", fontSize: "12px" }}
                                      >
                                        {travelerData.fullName ? travelerData.fullName.charAt(0).toUpperCase() : "T"}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {guideReview ? (
                                  <div>
                                    {/* Star Rating Score Bar */}
                                    <div
                                      className="p-2 mb-2 rounded d-flex align-items-center justify-content-between"
                                      style={{ background: "#fef3c7", border: "1px solid #fde68a" }}
                                    >
                                      <div className="d-flex align-items-center">
                                        <div className="mr-2">
                                          {Array.from({ length: 5 }).map((_, i) => (
                                            <i
                                              key={i}
                                              className={`fas fa-star ${
                                                i < (guideReview.rating || 0) ? "text-warning" : "text-light"
                                              }`}
                                              style={{ fontSize: "14px", marginRight: "3px" }}
                                            />
                                          ))}
                                        </div>
                                        <span className="font-weight-bold text-dark" style={{ fontSize: "14px" }}>
                                          {Number(guideReview.rating).toFixed(1)} / 5.0
                                        </span>
                                      </div>
                                      <small className="text-muted font-weight-500" style={{ fontSize: "11px" }}>
                                        <i className="far fa-clock mr-1" />
                                        {formatDate(guideReview.createdAt)}
                                      </small>
                                    </div>

                                    {/* Written Review Feedback */}
                                    <div>
                                      <small className="text-muted text-uppercase font-weight-bold d-block mb-1" style={{ fontSize: "10px", letterSpacing: "0.5px" }}>
                                        Feedback / Review Comment:
                                      </small>
                                      <div
                                        className="p-3 rounded bg-white text-dark shadow-xs"
                                        style={{
                                          fontSize: "13px",
                                          lineHeight: "1.6",
                                          border: "1px solid #e2e8f0",
                                          borderLeft: "3px solid #10b981",
                                          minHeight: "56px",
                                          fontStyle: guideReview.review ? "normal" : "italic",
                                        }}
                                      >
                                        {guideReview.review ? (
                                          <>
                                            <i className="fas fa-quote-left text-muted mr-2" style={{ fontSize: "11px" }} />
                                            {guideReview.review}
                                          </>
                                        ) : (
                                          <span className="text-muted">No written feedback was provided.</span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="text-center py-4 px-2">
                                    <div
                                      className="rounded-circle d-inline-flex align-items-center justify-content-center mb-2"
                                      style={{ width: "42px", height: "42px", background: "#f1f5f9", color: "#94a3b8" }}
                                    >
                                      <i className="far fa-star fa-lg" />
                                    </div>
                                    <h6 className="font-weight-bold text-dark mb-1" style={{ fontSize: "13px" }}>
                                      Guide Has Not Rated Yet
                                    </h6>
                                    <small className="text-muted d-block" style={{ fontSize: "12px" }}>
                                      <strong>{guideData.fullName || "Guide"}</strong> has not submitted a rating or review for <strong>{travelerData.fullName || "Traveler"}</strong> yet.
                                    </small>
                                  </div>
                                )}
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </div>
                      )}
                    </div>
                  </TabPane>

                  {/* ================= TAB 1: GUIDE DETAILS ================= */}
                  <TabPane tabId="1">
                    <div className="booking-info-card">
                      {/* Guide Profile Header */}
                      <div className="booking-profile-header">
                        <div className="booking-profile-avatar bg-gradient-success">
                          {guideData.fullName
                            ? guideData.fullName.charAt(0).toUpperCase()
                            : "G"}
                        </div>
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                            <div>
                              <h4 className="font-weight-bold mb-0 text-dark">
                                {guideData.fullName || "N/A"}
                              </h4>
                              <small className="text-muted">
                                {guideData.email || "No email provided"}
                              </small>
                            </div>
                            <div>
                              {guideData.isGuideVerified ? (
                                <Badge color="success" pill className="px-3 py-1">
                                  <i className="fas fa-check-circle mr-1" />
                                  Verified Guide
                                </Badge>
                              ) : guideData.isActive !== false ? (
                                <Badge color="info" pill className="px-3 py-1">
                                  <i className="fas fa-user-check mr-1" />
                                  Active Guide
                                </Badge>
                              ) : (
                                <Badge color="danger" pill className="px-3 py-1">
                                  <i className="fas fa-user-times mr-1" />
                                  Inactive
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Guide Details Grid */}
                      <div className="booking-detail-grid">
                        <div className="booking-detail-item">
                          <div className="booking-detail-icon green">
                            <i className="fas fa-user-tie" />
                          </div>
                          <div className="booking-detail-content">
                            <span className="label">Guide Name</span>
                            <span className="value">
                              {guideData.fullName || "N/A"}
                            </span>
                          </div>
                        </div>

                        <div className="booking-detail-item">
                          <div className="booking-detail-icon green">
                            <i className="fas fa-envelope" />
                          </div>
                          <div className="booking-detail-content">
                            <span className="label">Guide Email</span>
                            <span className="value">
                              {guideData.email || "N/A"}
                            </span>
                          </div>
                        </div>

                        <div className="booking-detail-item">
                          <div className="booking-detail-icon green">
                            <i className="fas fa-phone-alt" />
                          </div>
                          <div className="booking-detail-content">
                            <span className="label">Guide Mobile</span>
                            <span className="value">
                              {guideData.mobileNumber || "N/A"}
                            </span>
                          </div>
                        </div>

                        <div className="booking-detail-item">
                          <div className="booking-detail-icon green">
                            <i className="fas fa-globe-americas" />
                          </div>
                          <div className="booking-detail-content">
                            <span className="label">Guide Country</span>
                            <span className="value">
                              {guideData.country || "N/A"}
                            </span>
                          </div>
                        </div>

                        <div className="booking-detail-item">
                          <div className="booking-detail-icon green">
                            <i className="fas fa-dollar-sign" />
                          </div>
                          <div className="booking-detail-content">
                            <span className="label">Hourly Rate</span>
                            <span className="value text-success font-weight-bold">
                              {guideData.hourlyRate || 0}
                            </span>
                          </div>
                        </div>

                        <div className="booking-detail-item">
                          <div className="booking-detail-icon green">
                            <i className="fas fa-business-time" />
                          </div>
                          <div className="booking-detail-content">
                            <span className="label">Experience</span>
                            <span className="value">
                              {guideData.yearsOfExperience || 0} Years
                            </span>
                          </div>
                        </div>

                        {(guideData.averageRating || guideData.rating) ? (
                          <div className="booking-detail-item">
                            <div className="booking-detail-icon green">
                              <i className="fas fa-star" />
                            </div>
                            <div className="booking-detail-content">
                              <span className="label">Average Rating</span>
                              <span className="value text-warning font-weight-bold">
                                ★ {Number(guideData.averageRating || guideData.rating || 0).toFixed(1)}
                                {guideData.totalRatings !== undefined && (
                                  <small className="text-muted ml-1 font-weight-normal">
                                    ({guideData.totalRatings} {guideData.totalRatings === 1 ? "review" : "reviews"})
                                  </small>
                                )}
                              </span>
                            </div>
                          </div>
                        ) : null}

                        {guideData.location && (
                          <div className="booking-detail-item full-width">
                            <div className="booking-detail-icon green">
                              <i className="fas fa-map-marker-alt" />
                            </div>
                            <div className="booking-detail-content">
                              <span className="label">Location</span>
                              <span className="value">{guideData.location}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </TabPane>

                  {/* ================= TAB 2: TRAVELER DETAILS ================= */}
                  <TabPane tabId="2">
                    <div className="booking-info-card">
                      {/* Traveler Profile Header */}
                      <div className="booking-profile-header">
                        <div className="booking-profile-avatar bg-gradient-primary">
                          {travelerData.fullName
                            ? travelerData.fullName.charAt(0).toUpperCase()
                            : "T"}
                        </div>
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                            <div>
                              <h4 className="font-weight-bold mb-0 text-dark">
                                {travelerData.fullName || "N/A"}
                              </h4>
                              <small className="text-muted">
                                {travelerData.email || "No email provided"}
                              </small>
                            </div>
                            <div className="d-flex align-items-center gap-1">
                              {travelerData.isActive !== false ? (
                                <Badge color="success" pill className="px-3 py-1">
                                  <i className="fas fa-user-check mr-1" />
                                  Active Traveler
                                </Badge>
                              ) : (
                                <Badge color="danger" pill className="px-3 py-1">
                                  <i className="fas fa-user-times mr-1" />
                                  Inactive
                                </Badge>
                              )}
                              {travelerData.emailVerified && (
                                <Badge color="info" pill className="px-2 py-1 ml-1">
                                  <i className="fas fa-check mr-1" />
                                  Email Verified
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Traveler Details Grid */}
                      <div className="booking-detail-grid">
                        <div className="booking-detail-item">
                          <div className="booking-detail-icon blue">
                            <i className="fas fa-user" />
                          </div>
                          <div className="booking-detail-content">
                            <span className="label">Traveler Name</span>
                            <span className="value">
                              {travelerData.fullName || "N/A"}
                            </span>
                          </div>
                        </div>

                        <div className="booking-detail-item">
                          <div className="booking-detail-icon blue">
                            <i className="fas fa-envelope" />
                          </div>
                          <div className="booking-detail-content">
                            <span className="label">Traveler Email</span>
                            <span className="value">
                              {travelerData.email || "N/A"}
                            </span>
                          </div>
                        </div>

                        <div className="booking-detail-item">
                          <div className="booking-detail-icon blue">
                            <i className="fas fa-phone-alt" />
                          </div>
                          <div className="booking-detail-content">
                            <span className="label">Traveler Mobile</span>
                            <span className="value">
                              {travelerData.mobileNumber || "N/A"}
                            </span>
                          </div>
                        </div>

                        <div className="booking-detail-item">
                          <div className="booking-detail-icon blue">
                            <i className="fas fa-globe-americas" />
                          </div>
                          <div className="booking-detail-content">
                            <span className="label">Traveler Country</span>
                            <span className="value">
                              {travelerData.country || "N/A"}
                            </span>
                          </div>
                        </div>

                        {travelerData.gender && (
                          <div className="booking-detail-item">
                            <div className="booking-detail-icon blue">
                              <i className="fas fa-venus-mars" />
                            </div>
                            <div className="booking-detail-content">
                              <span className="label">Gender</span>
                              <span className="value text-capitalize">
                                {travelerData.gender}
                              </span>
                            </div>
                          </div>
                        )}

                        {travelerData.location && (
                          <div className="booking-detail-item">
                            <div className="booking-detail-icon blue">
                              <i className="fas fa-map-marker-alt" />
                            </div>
                            <div className="booking-detail-content">
                              <span className="label">Location</span>
                              <span className="value">{travelerData.location}</span>
                            </div>
                          </div>
                        )}

                        {(travelerData.averageRating || travelerData.rating) ? (
                          <div className="booking-detail-item">
                            <div className="booking-detail-icon blue">
                              <i className="fas fa-star" />
                            </div>
                            <div className="booking-detail-content">
                              <span className="label">Average Rating</span>
                              <span className="value text-warning font-weight-bold">
                                ★ {Number(travelerData.averageRating || travelerData.rating || 0).toFixed(1)}
                                {travelerData.totalRatings !== undefined && (
                                  <small className="text-muted ml-1 font-weight-normal">
                                    ({travelerData.totalRatings} {travelerData.totalRatings === 1 ? "review" : "reviews"})
                                  </small>
                                )}
                              </span>
                            </div>
                          </div>
                        ) : null}

                        <div className="booking-detail-item">
                          <div className="booking-detail-icon blue">
                            <i className="far fa-calendar-alt" />
                          </div>
                          <div className="booking-detail-content">
                            <span className="label">Registered On</span>
                            <span className="value">
                              {formatDate(travelerData.createdAt)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </TabPane>

                  {/* ================= TAB 4: RATINGS & REVIEWS ================= */}
                  {false && (
                  <TabPane tabId="4">
                    <div className="booking-info-card">
                      <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
                        <div>
                          <h4 className="font-weight-bold mb-0 text-dark">
                            <i className="fas fa-star text-warning mr-2" />
                            Booking Reviews & Ratings
                          </h4>
                          <small className="text-muted">
                            Ratings exchanged between traveler and guide for this booking
                          </small>
                        </div>
                        <Badge color="info" pill className="px-3 py-1">
                          {ratingsData.length} {ratingsData.length === 1 ? "Review" : "Reviews"} Total
                        </Badge>
                      </div>

                      <Row>
                        {/* DIV 1: Traveler's Rating to Guide */}
                        <Col md="6" className="mb-3">
                          <div
                            className="rounded h-100 overflow-hidden"
                            style={{
                              backgroundColor: "#ffffff",
                              border: travelerReview ? "1.5px solid #6366f1" : "1.5px dashed #cbd5e1",
                              boxShadow: travelerReview ? "0 4px 16px rgba(99, 102, 241, 0.1)" : "none",
                            }}
                          >
                            {/* Card Top Header */}
                            <div
                              className="p-3 d-flex align-items-center justify-content-between"
                              style={{
                                background: travelerReview
                                  ? "linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)"
                                  : "#f8fafc",
                                borderBottom: "1px solid #e2e8f0",
                              }}
                            >
                              <div className="d-flex align-items-center">
                                <div
                                  className="rounded-circle d-flex align-items-center justify-content-center mr-2 shadow-sm font-weight-bold"
                                  style={{ width: "28px", height: "28px", background: "#6366f1", color: "#ffffff", fontSize: "12px" }}
                                >
                                  <i className="fas fa-paper-plane" />
                                </div>
                                <div>
                                  <span className="font-weight-bold text-dark d-block" style={{ fontSize: "13px", lineHeight: "1.2" }}>
                                    Traveler ➔ Guide Review
                                  </span>
                                  <small className="text-muted" style={{ fontSize: "11px" }}>
                                    Given by <strong>{travelerData.fullName || "Traveler"}</strong> to <strong>{guideData.fullName || "Guide"}</strong>
                                  </small>
                                </div>
                              </div>

                              <div>
                                {travelerReview ? (
                                  <Badge
                                    color={travelerReview.isActive !== false ? "success" : "secondary"}
                                    pill
                                    className="px-2 py-1"
                                  >
                                    {travelerReview.isActive !== false ? "Active" : "Hidden"}
                                  </Badge>
                                ) : (
                                  <Badge color="light" pill className="text-muted border px-2 py-1" style={{ fontSize: "11px" }}>
                                    Not Rated Yet
                                  </Badge>
                                )}
                              </div>
                            </div>

                            {/* Card Body */}
                            <div className="p-3">
                              {/* WHO RATED WHOM FLOW */}
                              <div
                                className="p-2 mb-3 rounded d-flex align-items-center justify-content-between"
                                style={{
                                  background: travelerReview ? "#f8fafc" : "#fafafa",
                                  border: "1px solid #e2e8f0",
                                }}
                              >
                                {/* Giver: Traveler */}
                                <div className="d-flex align-items-center" style={{ flex: "1 1 0", minWidth: 0 }}>
                                  {travelerData.profileImage ? (
                                    <img
                                      src={travelerData.profileImage}
                                      alt={travelerData.fullName}
                                      className="rounded-circle mr-2 shadow-sm"
                                      style={{ width: "34px", height: "34px", minWidth: "34px", objectFit: "cover" }}
                                    />
                                  ) : (
                                    <div
                                      className="avatar avatar-sm rounded-circle mr-2 bg-gradient-primary text-white d-flex align-items-center justify-content-center shadow-sm font-weight-bold"
                                      style={{ width: "34px", height: "34px", minWidth: "34px", fontSize: "12px" }}
                                    >
                                      {travelerData.fullName ? travelerData.fullName.charAt(0).toUpperCase() : "T"}
                                    </div>
                                  )}
                                  <div className="overflow-hidden">
                                    <span className="font-weight-bold d-block text-dark text-truncate" style={{ fontSize: "12px", lineHeight: "1.2" }}>
                                      {travelerData.fullName || "Traveler"}
                                    </span>
                                    <Badge color="primary" pill style={{ fontSize: "9px", padding: "1px 5px", fontWeight: "600" }}>
                                      Reviewer (Traveler)
                                    </Badge>
                                  </div>
                                </div>

                                {/* Arrow Connector */}
                                <div className="text-center px-1 d-flex flex-column align-items-center" style={{ minWidth: "85px" }}>
                                  <span className="text-muted text-uppercase" style={{ fontSize: "9px", fontWeight: "700" }}>
                                    {travelerReview ? "Rates" : "Pending"}
                                  </span>
                                  <div className="d-flex align-items-center text-primary" style={{ fontSize: "13px" }}>
                                    <span style={{ height: "2px", width: "20px", background: "#6366f1", display: "inline-block" }}></span>
                                    <i className="fas fa-chevron-right" style={{ color: "#6366f1", marginLeft: "-2px" }} />
                                  </div>
                                </div>

                                {/* Receiver: Guide */}
                                <div className="d-flex align-items-center justify-content-end text-right" style={{ flex: "1 1 0", minWidth: 0 }}>
                                  <div className="overflow-hidden mr-2">
                                    <span className="font-weight-bold d-block text-dark text-truncate" style={{ fontSize: "12px", lineHeight: "1.2" }}>
                                      {guideData.fullName || "Guide"}
                                    </span>
                                    <Badge color="success" pill style={{ fontSize: "9px", padding: "1px 5px", fontWeight: "600" }}>
                                      Reviewed (Guide)
                                    </Badge>
                                  </div>
                                  {guideData.profileImage ? (
                                    <img
                                      src={guideData.profileImage}
                                      alt={guideData.fullName}
                                      className="rounded-circle shadow-sm"
                                      style={{ width: "34px", height: "34px", minWidth: "34px", objectFit: "cover" }}
                                    />
                                  ) : (
                                    <div
                                      className="avatar avatar-sm rounded-circle bg-gradient-success text-white d-flex align-items-center justify-content-center shadow-sm font-weight-bold"
                                      style={{ width: "34px", height: "34px", minWidth: "34px", fontSize: "12px" }}
                                    >
                                      {guideData.fullName ? guideData.fullName.charAt(0).toUpperCase() : "G"}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {travelerReview ? (
                                <div>
                                  {/* Star Rating Score Bar */}
                                  <div
                                    className="p-2 mb-2 rounded d-flex align-items-center justify-content-between"
                                    style={{ background: "#fef3c7", border: "1px solid #fde68a" }}
                                  >
                                    <div className="d-flex align-items-center">
                                      <div className="mr-2">
                                        {Array.from({ length: 5 }).map((_, i) => (
                                          <i
                                            key={i}
                                            className={`fas fa-star ${
                                              i < (travelerReview.rating || 0) ? "text-warning" : "text-light"
                                            }`}
                                            style={{ fontSize: "14px", marginRight: "3px" }}
                                          />
                                        ))}
                                      </div>
                                      <span className="font-weight-bold text-dark" style={{ fontSize: "14px" }}>
                                        {Number(travelerReview.rating).toFixed(1)} / 5.0
                                      </span>
                                    </div>
                                    <small className="text-muted font-weight-500" style={{ fontSize: "11px" }}>
                                      <i className="far fa-clock mr-1" />
                                      {formatDate(travelerReview.createdAt)}
                                    </small>
                                  </div>

                                  {/* Written Review Feedback */}
                                  <div>
                                    <small className="text-muted text-uppercase font-weight-bold d-block mb-1" style={{ fontSize: "10px", letterSpacing: "0.5px" }}>
                                      Feedback / Review Comment:
                                    </small>
                                    <div
                                      className="p-3 rounded bg-white text-dark shadow-xs"
                                      style={{
                                        fontSize: "13px",
                                        lineHeight: "1.6",
                                        border: "1px solid #e2e8f0",
                                        borderLeft: "3px solid #6366f1",
                                        minHeight: "56px",
                                        fontStyle: travelerReview.review ? "normal" : "italic",
                                      }}
                                    >
                                      {travelerReview.review ? (
                                        <>
                                          <i className="fas fa-quote-left text-muted mr-2" style={{ fontSize: "11px" }} />
                                          {travelerReview.review}
                                        </>
                                      ) : (
                                        <span className="text-muted">No written feedback was provided.</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="text-center py-4 px-2">
                                  <div
                                    className="rounded-circle d-inline-flex align-items-center justify-content-center mb-2"
                                    style={{ width: "42px", height: "42px", background: "#f1f5f9", color: "#94a3b8" }}
                                  >
                                    <i className="far fa-star fa-lg" />
                                  </div>
                                  <h6 className="font-weight-bold text-dark mb-1" style={{ fontSize: "13px" }}>
                                    Traveler Has Not Rated Yet
                                  </h6>
                                  <small className="text-muted d-block" style={{ fontSize: "12px" }}>
                                    <strong>{travelerData.fullName || "Traveler"}</strong> has not submitted a rating or review for <strong>{guideData.fullName || "Guide"}</strong> yet.
                                  </small>
                                </div>
                              )}
                            </div>
                          </div>
                        </Col>

                        {/* DIV 2: Guide's Rating to Traveler */}
                        <Col md="6" className="mb-3">
                          <div
                            className="rounded h-100 overflow-hidden"
                            style={{
                              backgroundColor: "#ffffff",
                              border: guideReview ? "1.5px solid #10b981" : "1.5px dashed #cbd5e1",
                              boxShadow: guideReview ? "0 4px 16px rgba(16, 185, 129, 0.1)" : "none",
                            }}
                          >
                            {/* Card Top Header */}
                            <div
                              className="p-3 d-flex align-items-center justify-content-between"
                              style={{
                                background: guideReview
                                  ? "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)"
                                  : "#f8fafc",
                                borderBottom: "1px solid #e2e8f0",
                              }}
                            >
                              <div className="d-flex align-items-center">
                                <div
                                  className="rounded-circle d-flex align-items-center justify-content-center mr-2 shadow-sm font-weight-bold"
                                  style={{ width: "28px", height: "28px", background: "#10b981", color: "#ffffff", fontSize: "12px" }}
                                >
                                  <i className="fas fa-paper-plane" />
                                </div>
                                <div>
                                  <span className="font-weight-bold text-dark d-block" style={{ fontSize: "13px", lineHeight: "1.2" }}>
                                    Guide ➔ Traveler Review
                                  </span>
                                  <small className="text-muted" style={{ fontSize: "11px" }}>
                                    Given by <strong>{guideData.fullName || "Guide"}</strong> to <strong>{travelerData.fullName || "Traveler"}</strong>
                                  </small>
                                </div>
                              </div>

                              <div>
                                {guideReview ? (
                                  <Badge
                                    color={guideReview.isActive !== false ? "success" : "secondary"}
                                    pill
                                    className="px-2 py-1"
                                  >
                                    {guideReview.isActive !== false ? "Active" : "Hidden"}
                                  </Badge>
                                ) : (
                                  <Badge color="light" pill className="text-muted border px-2 py-1" style={{ fontSize: "11px" }}>
                                    Not Rated Yet
                                  </Badge>
                                )}
                              </div>
                            </div>

                            {/* Card Body */}
                            <div className="p-3">
                              {/* WHO RATED WHOM FLOW */}
                              <div
                                className="p-2 mb-3 rounded d-flex align-items-center justify-content-between"
                                style={{
                                  background: guideReview ? "#f8fafc" : "#fafafa",
                                  border: "1px solid #e2e8f0",
                                }}
                              >
                                {/* Giver: Guide */}
                                <div className="d-flex align-items-center" style={{ flex: "1 1 0", minWidth: 0 }}>
                                  {guideData.profileImage ? (
                                    <img
                                      src={guideData.profileImage}
                                      alt={guideData.fullName}
                                      className="rounded-circle mr-2 shadow-sm"
                                      style={{ width: "34px", height: "34px", minWidth: "34px", objectFit: "cover" }}
                                    />
                                  ) : (
                                    <div
                                      className="avatar avatar-sm rounded-circle mr-2 bg-gradient-success text-white d-flex align-items-center justify-content-center shadow-sm font-weight-bold"
                                      style={{ width: "34px", height: "34px", minWidth: "34px", fontSize: "12px" }}
                                    >
                                      {guideData.fullName ? guideData.fullName.charAt(0).toUpperCase() : "G"}
                                    </div>
                                  )}
                                  <div className="overflow-hidden">
                                    <span className="font-weight-bold d-block text-dark text-truncate" style={{ fontSize: "12px", lineHeight: "1.2" }}>
                                      {guideData.fullName || "Guide"}
                                    </span>
                                    <Badge color="success" pill style={{ fontSize: "9px", padding: "1px 5px", fontWeight: "600" }}>
                                      Reviewer (Guide)
                                    </Badge>
                                  </div>
                                </div>

                                {/* Arrow Connector */}
                                <div className="text-center px-1 d-flex flex-column align-items-center" style={{ minWidth: "85px" }}>
                                  <span className="text-muted text-uppercase" style={{ fontSize: "9px", fontWeight: "700" }}>
                                    {guideReview ? "Rates" : "Pending"}
                                  </span>
                                  <div className="d-flex align-items-center text-success" style={{ fontSize: "13px" }}>
                                    <span style={{ height: "2px", width: "20px", background: "#10b981", display: "inline-block" }}></span>
                                    <i className="fas fa-chevron-right" style={{ color: "#10b981", marginLeft: "-2px" }} />
                                  </div>
                                </div>

                                {/* Receiver: Traveler */}
                                <div className="d-flex align-items-center justify-content-end text-right" style={{ flex: "1 1 0", minWidth: 0 }}>
                                  <div className="overflow-hidden mr-2">
                                    <span className="font-weight-bold d-block text-dark text-truncate" style={{ fontSize: "12px", lineHeight: "1.2" }}>
                                      {travelerData.fullName || "Traveler"}
                                    </span>
                                    <Badge color="primary" pill style={{ fontSize: "9px", padding: "1px 5px", fontWeight: "600" }}>
                                      Reviewed (Traveler)
                                    </Badge>
                                  </div>
                                  {travelerData.profileImage ? (
                                    <img
                                      src={travelerData.profileImage}
                                      alt={travelerData.fullName}
                                      className="rounded-circle shadow-sm"
                                      style={{ width: "34px", height: "34px", minWidth: "34px", objectFit: "cover" }}
                                    />
                                  ) : (
                                    <div
                                      className="avatar avatar-sm rounded-circle bg-gradient-primary text-white d-flex align-items-center justify-content-center shadow-sm font-weight-bold"
                                      style={{ width: "34px", height: "34px", minWidth: "34px", fontSize: "12px" }}
                                    >
                                      {travelerData.fullName ? travelerData.fullName.charAt(0).toUpperCase() : "T"}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {guideReview ? (
                                <div>
                                  {/* Star Rating Score Bar */}
                                  <div
                                    className="p-2 mb-2 rounded d-flex align-items-center justify-content-between"
                                    style={{ background: "#fef3c7", border: "1px solid #fde68a" }}
                                  >
                                    <div className="d-flex align-items-center">
                                      <div className="mr-2">
                                        {Array.from({ length: 5 }).map((_, i) => (
                                          <i
                                            key={i}
                                            className={`fas fa-star ${
                                              i < (guideReview.rating || 0) ? "text-warning" : "text-light"
                                            }`}
                                            style={{ fontSize: "14px", marginRight: "3px" }}
                                          />
                                        ))}
                                      </div>
                                      <span className="font-weight-bold text-dark" style={{ fontSize: "14px" }}>
                                        {Number(guideReview.rating).toFixed(1)} / 5.0
                                      </span>
                                    </div>
                                    <small className="text-muted font-weight-500" style={{ fontSize: "11px" }}>
                                      <i className="far fa-clock mr-1" />
                                      {formatDate(guideReview.createdAt)}
                                    </small>
                                  </div>

                                  {/* Written Review Feedback */}
                                  <div>
                                    <small className="text-muted text-uppercase font-weight-bold d-block mb-1" style={{ fontSize: "10px", letterSpacing: "0.5px" }}>
                                      Feedback / Review Comment:
                                    </small>
                                    <div
                                      className="p-3 rounded bg-white text-dark shadow-xs"
                                      style={{
                                        fontSize: "13px",
                                        lineHeight: "1.6",
                                        border: "1px solid #e2e8f0",
                                        borderLeft: "3px solid #10b981",
                                        minHeight: "56px",
                                        fontStyle: guideReview.review ? "normal" : "italic",
                                      }}
                                    >
                                      {guideReview.review ? (
                                        <>
                                          <i className="fas fa-quote-left text-muted mr-2" style={{ fontSize: "11px" }} />
                                          {guideReview.review}
                                        </>
                                      ) : (
                                        <span className="text-muted">No written feedback was provided.</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="text-center py-4 px-2">
                                  <div
                                    className="rounded-circle d-inline-flex align-items-center justify-content-center mb-2"
                                    style={{ width: "42px", height: "42px", background: "#f1f5f9", color: "#94a3b8" }}
                                  >
                                    <i className="far fa-star fa-lg" />
                                  </div>
                                  <h6 className="font-weight-bold text-dark mb-1" style={{ fontSize: "13px" }}>
                                    Guide Has Not Rated Yet
                                  </h6>
                                  <small className="text-muted d-block" style={{ fontSize: "12px" }}>
                                    <strong>{guideData.fullName || "Guide"}</strong> has not submitted a rating or review for <strong>{travelerData.fullName || "Traveler"}</strong> yet.
                                  </small>
                                </div>
                              )}
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </TabPane>
                  )}
                </TabContent>
              </>
            ) : null}
          </ModalBody>

          <ModalFooter className="border-top pt-3">
            {/*
            {bookingData.status === "pending" && (
              <>
                <Button
                  color="success"
                  size="sm"
                  onClick={() => handleStatusChange(bookingData.uuid, "accepted")}
                  disabled={actionLoading}
                >
                  <i className="fas fa-check mr-1" />
                  Accept Booking
                </Button>
                <Button
                  color="warning"
                  size="sm"
                  onClick={() => handleStatusChange(bookingData.uuid, "rejected")}
                  disabled={actionLoading}
                >
                  <i className="fas fa-ban mr-1" />
                  Reject Booking
                </Button>
              </>
            )}
            */}




            <Button color="secondary" size="sm" onClick={closeViewModal}>
              Close
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </>
  );
};

export default GuideBookingManagement;
